import { useEffect, useState } from 'react';
import { getApiUrl, type ActiveMind } from '../services/api';

/**
 * THE MIND VIEW (overhaul P3b) — her self-map and her receipts, rendered.
 * Everything here is read-only and carries its provenance; absence renders as an honest
 * sentence, never a fake panel (R-DASH parity rule).
 */

interface MapEntry {
  id: string;
  name: string;
  description: string;
  draft: boolean;
  status: 'live' | 'dead' | 'protected';
  protected_reason?: string;
  status_evidence: string;
  last_verified_at: string;
  channel: string;
}

interface EmailRow {
  id: string;
  direction: 'inbound' | 'outbound';
  from_address: string;
  to_address: string;
  subject: string | null;
  snippet: string;
  at: string;
  replied: boolean;
}

interface Receipt {
  id: string;
  kind: string;
  input: string;
  status: string;
  error: string | null;
  artifact_id: string | null;
  created_at: string;
  draft_preserved: boolean;
}

type Tab = 'map' | 'receipts' | 'email';

export function MindView({ token, mind, mindName, onClose }: {
  token: string;
  mind: ActiveMind;
  mindName: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>('map');
  const [query, setQuery] = useState('');
  const [map, setMap] = useState<{ entries: MapEntry[]; version: number } | null>(null);
  const [mapAbsent, setMapAbsent] = useState<string | null>(null);
  const [receipts, setReceipts] = useState<Receipt[] | null>(null);
  const [receiptsAbsent, setReceiptsAbsent] = useState<string | null>(null);
  const [emails, setEmails] = useState<EmailRow[] | null>(null);
  const [emailsAbsent, setEmailsAbsent] = useState<string | null>(null);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${getApiUrl(mind)}/mind/self-map${query ? `?q=${encodeURIComponent(query)}` : ''}`, { headers })
      .then(r => r.json())
      .then((d: { available: boolean; map?: { entries: MapEntry[]; version: number }; reason?: string }) => {
        if (d.available && d.map) { setMap(d.map); setMapAbsent(null); }
        else setMapAbsent(d.reason ?? 'not available');
      })
      .catch(() => setMapAbsent('could not be read this turn'));
  }, [token, mind, query]);

  useEffect(() => {
    fetch(`${getApiUrl(mind)}/mind/receipts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then((d: { available: boolean; receipts?: Receipt[]; reason?: string }) => {
        if (d.available && d.receipts) { setReceipts(d.receipts); setReceiptsAbsent(null); }
        else setReceiptsAbsent(d.reason ?? 'not available');
      })
      .catch(() => setReceiptsAbsent('could not be read this turn'));
  }, [token, mind]);

  useEffect(() => {
    fetch(`${getApiUrl(mind)}/mind/emails`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.status === 401 ? { available: false, reason: 'creator only' } : r.json())
      .then((d: { available: boolean; emails?: EmailRow[]; reason?: string }) => {
        if (d.available && d.emails) { setEmails(d.emails); setEmailsAbsent(null); }
        else setEmailsAbsent(d.reason ?? 'not available');
      })
      .catch(() => setEmailsAbsent('could not be read this turn'));
  }, [token, mind]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  return (
    <div className="mind-view" role="dialog" aria-label={`${mindName}'s mind`}>
      <div className="mind-view-header">
        <div className="mind-view-title">
          <h1>How {mindName} works</h1>
          <p className="mind-view-sub">
            Historical architecture map. Each entry shows when it was last checked; it does not report current activity.
          </p>
        </div>
        <button className="header-icon-btn" onClick={onClose} aria-label="Close mind view">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <div className="mind-view-tabs">
        <button className={`mind-tab ${tab === 'map' ? 'active' : ''}`} onClick={() => setTab('map')}>Self-map</button>
        <button className={`mind-tab ${tab === 'receipts' ? 'active' : ''}`} onClick={() => setTab('receipts')}>Legacy receipts</button>
        <button className={`mind-tab ${tab === 'email' ? 'active' : ''}`} onClick={() => setTab('email')}>Email</button>
      </div>

      {tab === 'map' && (
        <div className="mind-view-body">
          <input
            className="mind-search"
            placeholder="Search her map — try “voice”, “amanda”, “dead”…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {mapAbsent && <p className="mind-absent">The map {mapAbsent === 'not built for this mind yet' ? `is not built for ${mindName} yet` : `could not be read: ${mapAbsent}`}.</p>}
          {map && (
            <div className="map-grid">
              {map.entries.map(e => (
                <article key={e.id} className={`map-card status-${e.status}`}>
                  <header className="map-card-head">
                    <h3>{e.name}</h3>
                    <span className={`status-badge status-${e.status}`}>{e.status}</span>
                  </header>
                  <p className="map-card-desc">{e.description}</p>
                  <footer className="map-card-foot">
                    <details>
                      <summary>evidence · verified {e.last_verified_at}</summary>
                      <p className="map-card-evidence">{e.status_evidence}</p>
                      {e.protected_reason && <p className="map-card-evidence">protected: {e.protected_reason}</p>}
                    </details>
                  </footer>
                </article>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'receipts' && (
        <div className="mind-view-body">
          <p className="mind-section-note">
            Requests from the earlier controller. Hannah’s resident-runtime actions are recorded separately; this list does not show everything she has done.
          </p>
          {receiptsAbsent && <p className="mind-absent">Receipts {receiptsAbsent === 'not built for this mind yet' ? `are not built for ${mindName} yet` : `could not be read: ${receiptsAbsent}`}.</p>}
          {receipts && receipts.length === 0 && <p className="mind-absent">No requests recorded by the earlier controller.</p>}
          {receipts && receipts.length > 0 && (
            <ul className="receipt-list">
              {receipts.map(r => (
                <li key={r.id} className={`receipt-row status-${r.status}`}>
                  <span className={`receipt-kind kind-${r.kind}`}>{r.kind === 'write_down' ? 'write' : r.kind}</span>
                  <span className="receipt-input" title={r.input}>{r.input}</span>
                  <span className={`receipt-status status-${r.status}`}>
                    {r.status === 'resolved' ? 'done' : r.status === 'failed' ? 'FAILED' : 'running'}
                  </span>
                  <span className="receipt-meta">
                    {r.status === 'failed' && r.error ? <span className="receipt-error" title={r.error}>{r.error.slice(0, 80)}</span> : null}
                    {r.status === 'failed' && r.draft_preserved ? <span className="receipt-kept">words preserved</span> : null}
                    {r.artifact_id ? <span className="receipt-artifact" title={r.artifact_id}>artifact {r.artifact_id.slice(0, 8)}</span> : null}
                    <time>{new Date(r.created_at.replace(' ', 'T') + 'Z').toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</time>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {tab === 'email' && (
        <div className="mind-view-body">
          <p className="mind-section-note">
            Her mailbox record — who she has written to and who has written her. Snippets only; the letters themselves stay in her record.
          </p>
          {emailsAbsent && <p className="mind-absent">Emails {emailsAbsent === 'creator only' ? 'are visible to Erika only' : `could not be read: ${emailsAbsent}`}.</p>}
          {emails && emails.length === 0 && <p className="mind-absent">No emails recorded.</p>}
          {emails && emails.length > 0 && (
            <ul className="receipt-list">
              {emails.map(m => (
                <li key={m.id} className={`receipt-row email-${m.direction}`}>
                  <span className={`receipt-kind kind-${m.direction}`}>{m.direction === 'inbound' ? 'in' : 'out'}</span>
                  <span className="email-parties" title={`${m.from_address} → ${m.to_address}`}>
                    {m.direction === 'inbound' ? m.from_address : m.to_address}
                  </span>
                  <span className="receipt-input" title={m.snippet}>{m.subject || '(no subject)'}</span>
                  <span className="receipt-meta">
                    {m.direction === 'inbound' && !m.replied && <span className="email-unanswered">unanswered</span>}
                    {m.replied && <span className="receipt-kept">replied</span>}
                    <time>{new Date(m.at.replace(' ', 'T') + 'Z').toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</time>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
